import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import {
  LayoutDashboard,
  Receipt,
  FolderTree,
  Users,
  Wallet,
  CreditCard,
  ListChecks,
  Building2,
  TrendingUp,
  ChevronRight,
  LogOut,
  PieChart,
  FileText,
  Layers,
  ArrowLeft,
  Shield,
  Lock,
  Calculator,
  LineChart,
  Briefcase,
  UserCog,
  Mail,
  Bot
} from 'lucide-react';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

// Módulos base (siempre incluidos)
const navItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', modulo: 'Dashboard' },
  { path: '/facturacion', icon: FileText, label: 'Facturación', modulo: 'Facturación' },
  { path: '/movimientos', icon: Receipt, label: 'Movimientos', modulo: 'Movimientos' },
  { path: '/categorias', icon: FolderTree, label: 'Categorías', modulo: 'Categorías' },
  { path: '/clientes-pagos', icon: Users, label: 'Clientes', modulo: 'Clientes' },
  { path: '/flujo-caja', icon: Wallet, label: 'Flujo de Caja', modulo: 'Flujo de Caja' },
  { path: '/estado-resultados', icon: FileText, label: 'Estado Resultados', modulo: 'Estado de Resultados' },
  { path: '/pagos', icon: CreditCard, label: 'Pagos', modulo: 'Pagos' },
  { path: '/prioridades', icon: ListChecks, label: 'Prioridades', modulo: 'Prioridades' },
  { path: '/rentabilidad', icon: PieChart, label: 'Rentabilidad', modulo: 'Rentabilidad' },
];

// Módulos Premium (requieren activación por pago)
const premiumItems = [
  { id: 'presupuestos', path: '/presupuestos', icon: Calculator, label: 'Presupuestos' },
  { id: 'forecast', path: '/forecast', icon: LineChart, label: 'Forecast' },
  { id: 'proyectos', path: '/proyectos', icon: Briefcase, label: 'Proyectos' },
  { id: 'equipo', path: '/equipo', icon: UserCog, label: 'Equipo' },
  { id: 'reportes_auto', path: '/reportes-auto', icon: Mail, label: 'Reportes Auto' },
  { id: 'ai_advisor', path: '/ai-advisor', icon: Bot, label: 'AI Advisor' },
];

// Items del panel administrativo (para Super Admin)
const adminItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Panel de Control' },
  { path: '/admin/empresas', icon: Building2, label: 'Empresas' },
  { path: '/admin/marcas', icon: Layers, label: 'Marcas y Planes' },
  { path: '/admin/usuarios', icon: Users, label: 'Usuarios' },
];

export const Sidebar = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedMarca, setSelectedMarca] = useState(null);
  const [modulosPremiumActivos, setModulosPremiumActivos] = useState([]);
  const [lockedModuleDialog, setLockedModuleDialog] = useState({ open: false, modulo: null });

  // Detectar si hay una marca seleccionada
  useEffect(() => {
    const marcaId = localStorage.getItem('selectedMarcaId');
    const marcaNombre = localStorage.getItem('selectedMarcaNombre');
    if (marcaId && marcaNombre) {
      setSelectedMarca({ id: marcaId, nombre: marcaNombre });
    }
  }, [location]);

  // Cargar módulos premium activos
  useEffect(() => {
    const loadModulosPremium = async () => {
      try {
        const marcaId = localStorage.getItem('selectedMarcaId');
        if (marcaId) {
          const response = await api.get(`/marca/${marcaId}/modulos-premium`);
          const activos = response.data.filter(m => m.activo).map(m => m.id);
          setModulosPremiumActivos(activos);
        } else if (user?.cliente_id) {
          // For regular users, get from their marca
          const response = await api.get('/usuario/modulos');
          const activos = response.data.modulos_premium.filter(m => m.activo).map(m => m.id);
          setModulosPremiumActivos(activos);
        }
      } catch (error) {
        console.error('Error loading premium modules:', error);
      }
    };
    
    if (!isSuperAdmin || selectedMarca) {
      loadModulosPremium();
    }
  }, [selectedMarca, user, isSuperAdmin]);

  const handleLogout = () => {
    localStorage.removeItem('selectedMarcaId');
    localStorage.removeItem('selectedMarcaNombre');
    localStorage.removeItem('selectedClienteId');
    localStorage.removeItem('selectedClienteNombre');
    logout();
    navigate('/login');
  };

  const handleExitMarca = () => {
    localStorage.removeItem('selectedMarcaId');
    localStorage.removeItem('selectedMarcaNombre');
    localStorage.removeItem('selectedClienteId');
    localStorage.removeItem('selectedClienteNombre');
    setSelectedMarca(null);
    navigate('/admin');
  };

  const handleLockedModuleClick = (modulo) => {
    setLockedModuleDialog({ open: true, modulo });
  };

  const isModuloPremiumActivo = (moduloId) => {
    return modulosPremiumActivos.includes(moduloId);
  };

  const isInAdminPanel = location.pathname.startsWith('/admin');
  const showOperativeMenu = !isSuperAdmin || (isSuperAdmin && selectedMarca && !isInAdminPanel);

  return (
    <div className="flex flex-col h-full">
      {/* Header with Logo and Logout Button */}
      <div className="p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
                FinScope
              </h1>
              <p className="text-xs text-muted-foreground">Inteligencia Financiera</p>
            </div>
          </div>
          <Button
            variant="destructive"
            size="icon"
            onClick={handleLogout}
            data-testid="logout-btn"
            title="Cerrar Sesión"
            className="h-9 w-9 flex-shrink-0"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* User info */}
      <div className="px-4 py-2 border-b border-border bg-gray-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-primary">
              {user?.nombre?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground truncate">{user?.nombre}</p>
            <div className="flex items-center gap-1">
              {isSuperAdmin && (
                <Badge variant="secondary" className="text-[10px] py-0 px-1">
                  <Shield className="w-3 h-3 mr-0.5" />
                  Admin
                </Badge>
              )}
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Marca seleccionada (para Super Admin) */}
      {isSuperAdmin && selectedMarca && !isInAdminPanel && (
        <div className="px-3 py-2 bg-primary/5 border-b border-primary/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-primary truncate">{selectedMarca.nombre}</span>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleExitMarca}
              className="h-7 px-2 text-xs"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Salir
            </Button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 p-3 pb-32 min-h-0">
        
        {/* Menu Admin para Super Admin sin marca seleccionada */}
        {isSuperAdmin && (isInAdminPanel || !selectedMarca) && (
          <div className="space-y-1">
            <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Administración FinScope
            </p>
            {adminItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-item relative ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </NavLink>
            ))}
          </div>
        )}

        {/* Menu Operativo (Admin/Viewer o Super Admin dentro de una marca) */}
        {showOperativeMenu && (
          <div className="space-y-1">
            {!isSuperAdmin && (
              <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                Módulos
              </p>
            )}
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) =>
                  `sidebar-item relative ${isActive ? 'active' : ''}`
                }
                data-testid={`nav-${item.path.replace('/', '')}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
              </NavLink>
            ))}
            
            {/* Módulos Premium */}
            <Separator className="my-3" />
            <p className="px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Módulos Premium
            </p>
            {premiumItems.map((item) => {
              const isActivo = isModuloPremiumActivo(item.id);
              
              if (isActivo) {
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className={({ isActive }) =>
                      `sidebar-item relative ${isActive ? 'active' : ''}`
                    }
                    data-testid={`nav-${item.id}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="flex-1">{item.label}</span>
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </NavLink>
                );
              }
              
              // Módulo bloqueado
              return (
                <button
                  key={item.path}
                  onClick={() => handleLockedModuleClick(item)}
                  className="sidebar-item relative w-full opacity-50 cursor-not-allowed hover:bg-transparent"
                  data-testid={`nav-${item.id}-locked`}
                >
                  <item.icon className="w-5 h-5 text-muted-foreground" />
                  <span className="flex-1 text-muted-foreground">{item.label}</span>
                  <Lock className="w-4 h-4 text-muted-foreground" />
                </button>
              );
            })}
            
          </div>
        )}

        {/* Link para volver al panel admin (cuando está en vista operativa) */}
        {isSuperAdmin && selectedMarca && !isInAdminPanel && (
          <>
            <Separator className="my-3" />
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={handleExitMarca}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Panel Admin
            </Button>
          </>
        )}
      </nav>

      {/* Dialog para módulo bloqueado */}
      <Dialog open={lockedModuleDialog.open} onOpenChange={(open) => setLockedModuleDialog({ ...lockedModuleDialog, open })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-500" />
              Módulo No Disponible
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-lg border border-amber-200 mb-4">
                {lockedModuleDialog.modulo && (
                  <>
                    <lockedModuleDialog.modulo.icon className="w-8 h-8 text-amber-600" />
                    <div>
                      <p className="font-semibold text-amber-800">{lockedModuleDialog.modulo?.label}</p>
                      <p className="text-sm text-amber-700">Módulo Premium</p>
                    </div>
                  </>
                )}
              </div>
              <p className="text-slate-600">
                Este módulo requiere activación. Para desbloquearlo, contacta a tu administrador de FinScope o solicita la activación.
              </p>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>¿Interesado?</strong> Contacta a soporte para conocer más sobre este módulo y cómo activarlo.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};
